import React, { useContext, useEffect } from 'react'
import { Button, Typography, Grid, CircularProgress, FormControl, Select, MenuItem, TextField } from '@material-ui/core'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { withRouter } from 'react-router'
import StripeElements from '../steps/stripe'
import { useTranslation } from 'react-i18next'
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js'
import Axios from 'axios'
import { map, size } from 'lodash'
import { useSnackbar } from 'notistack'
import PaymentLogo from '../PaymentLogo/PaymentLogo'
import { COUNTRY_NAMES_BY_CODE, ISO_3166_COUNTRIES } from '../constants'
import { UserContext } from '../utils/UserContext'

const PaymentMethodForm = (props) => {
  const { t } = useTranslation()
  const booking = props.booking
  const stripe = useStripe()
  const elements = useElements()
  const property = { currency: 'EUR' }
  const { user } = useContext(UserContext)
  const { enqueueSnackbar } = useSnackbar()
  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  })
  const { handleSubmit, errors, formState, setError, control, watch, clearErrors } = methods

  const values = watch()

  const onSubmit = async () => {
    try {
      if (size(errors) !== 0) {
        return
      }
      const { error, token } = await stripe.createToken(elements.getElement(CardNumberElement), { currency: property.currency })
      if (error) {
        throw new Error(error)
      }
      const payload = {
        id: booking.id,
        card_token: token.id,
        card_holder_name: values.card_holder_name,
        card_holder_email: values.card_holder_email,
        card_holder_address: values.card_holder_address,
        card_holder_city: values.card_holder_city,
        card_holder_country: values.card_holder_country,
        card_holder_zip: values.card_holder_zip,
      }
      await Axios.post(`${process.env.REACT_APP_CHANGE_PAYMENT_METHOD_API}`, payload)
      enqueueSnackbar(t('changePaymentMethod.form.success'), { variant: 'success' })
      setTimeout(() => {
        window.location.href =
          user?.lang === 'en'
            ? `${process.env.REACT_APP_WEBSITE}/en/bookings?id=${booking.id}`
            : `${process.env.REACT_APP_WEBSITE}/it/prenotazioni?id=${booking.id}`
      }, 2000)
    } catch (e) {
      console.log(e)
      enqueueSnackbar(t('changePaymentMethod.form.error'), { variant: 'error' })
    }
  }

  useEffect(() => {
    if (!values.card_holder_name) {
      setError('card_holder_name', {
        type: 'manual',
        message: 'this field is required',
      })
    } else {
      clearErrors('card_holder_name')
    }
  }, [values.card_holder_name])

  useEffect(() => {
    if (!values.card_holder_email) {
      setError('card_holder_email', {
        type: 'manual',
        message: 'this field is required',
      })
    } else {
      clearErrors('card_holder_email')
    }
  }, [values.card_holder_email])

  useEffect(() => {
    if (!values.card_holder_address) {
      setError('card_holder_address', {
        type: 'manual',
        message: 'this field is required',
      })
    } else {
      clearErrors('card_holder_address')
    }
  }, [values.card_holder_address])

  useEffect(() => {
    if (!values.card_holder_city) {
      setError('card_holder_city', {
        type: 'manual',
        message: 'this field is required',
      })
    } else {
      clearErrors('card_holder_city')
    }
  }, [values.card_holder_city])

  useEffect(() => {
    if (!values.card_holder_country) {
      setError('card_holder_country', {
        type: 'manual',
        message: 'this field is required',
      })
    } else if (values?.card_holder_country?.length !== 2) {
      setError('card_holder_country', {
        type: 'manual',
        message: 'this field must be 2 characters long',
      })
    } else {
      clearErrors('card_holder_country')
    }
  }, [values.card_holder_country])

  useEffect(() => {
    if (!values.card_holder_zip) {
      setError('card_holder_zip', {
        type: 'manual',
        message: 'this field is required',
      })
    } else {
      clearErrors('card_holder_zip')
    }
  }, [values.card_holder_zip])

  useEffect(() => {
    if (values.payment_type !== 'card' || booking.status === 'PENDING') {
      clearErrors([
        'card_holder_name',
        'card_holder_email',
        'card_holder_address',
        'card_holder_city',
        'card_holder_country',
        'card_holder_zip',
      ])
    } else {
      setError('card_holder_name', {
        type: 'manual',
        message: 'this field is required',
      })
      setError('card_holder_email', {
        type: 'manual',
        message: 'this field is required',
      })
      setError('card_holder_address', {
        type: 'manual',
        message: 'this field is required',
      })
      setError('card_holder_city', {
        type: 'manual',
        message: 'this field is required',
      })
      setError('card_holder_country', {
        type: 'manual',
        message: 'this field is required',
      })
      setError('card_holder_zip', {
        type: 'manual',
        message: 'this field is required',
      })
    }
  }, [values.payment_type])

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="h-full pt-16 pb-24">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h4">{t('changePaymentMethod.title')}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1">{t('changePaymentMethod.subtitle')}</Typography>
          </Grid>
        </Grid>
        <Grid container item xs={12} spacing={2}>
          <StripeElements {...props} />
          <Grid item xs={12} lg={10}>
            <Typography variant="body1">{t('booking.paymentInfo.cardHolderName')}</Typography>
            <Controller
              name="card_holder_name"
              control={control}
              rules={{ required: true }}
              as={<TextField fullWidth error={errors.card_holder_name} variant="outlined" />}
            />
          </Grid>
          <Grid item xs={12} lg={10}>
            <Typography variant="body1">{t('booking.paymentInfo.cardHolderEmail')}</Typography>
            <Controller
              name="card_holder_email"
              control={control}
              rules={{ required: true }}
              as={<TextField type="email" fullWidth error={errors.card_holder_email} variant="outlined" />}
            />
          </Grid>
          <Grid item xs={12} lg={10}>
            <Typography variant="body1">{t('booking.paymentInfo.cardHolderAddress')}</Typography>
            <Controller
              name="card_holder_address"
              control={control}
              rules={{ required: true }}
              as={<TextField type="text" fullWidth error={errors.card_holder_address} variant="outlined" />}
            />
          </Grid>
          <Grid item xs={12} lg={10}>
            <Typography variant="body1">{t('booking.paymentInfo.cardHolderCity')}</Typography>
            <Controller
              name="card_holder_city"
              control={control}
              rules={{ required: true }}
              as={<TextField type="text" fullWidth error={errors.card_holder_city} variant="outlined" />}
            />
          </Grid>
          <Grid item xs={12} lg={10}>
            <Typography variant="body1">{t('booking.paymentInfo.cardHolderZip')}</Typography>
            <Controller
              name="card_holder_zip"
              control={control}
              rules={{ required: true }}
              as={<TextField type="text" fullWidth error={errors.card_holder_zip} variant="outlined" />}
            />
          </Grid>
          <Grid item xs={12} lg={10}>
            <FormControl fullWidth variant="outlined">
              <Typography variant="body1">{t('booking.paymentInfo.cardHolderCountry')}</Typography>
              <Controller
                name="card_holder_country"
                control={control}
                error={errors.card_holder_country}
                rules={{ required: true }}
                as={
                  <Select>
                    {map(ISO_3166_COUNTRIES, (value) => (
                      <MenuItem key={value} value={value}>
                        {value} - {COUNTRY_NAMES_BY_CODE[value]}
                      </MenuItem>
                    ))}
                  </Select>
                }
              />
            </FormControl>
          </Grid>
        </Grid>
        <Grid item xs={12} lg={10}>
          <PaymentLogo />
        </Grid>
        <div className="md:fixed z-10 bottom-0 left-0 w-full pb-8 md:p-0">
          <div className="w-full flex flex-col items-center bg-white border-t border-gray-200 text-gray-600">
            <div className="w-full lg:w-10/12 m-auto flex flex-row-reverse p-4 lg:py-6 justify-between items-center">
              <Button
                color="primary"
                variant="contained"
                size="large"
                type="submit"
                disabled={formState.isSubmitting || size(errors) > 0}
              >
                {formState.isSubmitting && <CircularProgress color="white" />}
                {!formState.isSubmitting && t('form.complete')}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default withRouter(PaymentMethodForm)
